import { OrganizationFleet, OrganizationRules } from './OrganizationTools';
import { useEffect, useState } from 'react';
import type { Organization, OrganizationMember } from '@apos/shared';
import { Button, Chip, Field, Icon, Panel, TextInput } from '../../components/ui';
import { request, errorMessage } from '../../lib/api';
import { useSession } from '../../state/session';
import { DataSourcesPanel } from './DataSourcesPanel';
import { Notice } from './pageParts';
import { useAccount } from '../../state/account';
import { publicUrl } from '../../lib/publicUrl';

type Mine = { organization: Organization | null; organizations: Organization[]; pending: Array<{ id: string; name: string }> };
export const MIN_ORG_QUERY = 3;
const ACTIVE_ORG_KEY = 'airview:active-organization';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';

export function OrganizationPanel({ setupIntent }: { setupIntent?: 'join' | 'create' } = {}) {
  const me = useSession((s) => s.user);
  const [tab, setTab] = useState<'people'|'sources'|'fleet'|'rules'>('people');
  const [invited, setInvited] = useState<{id:string;name:string}|null>(null);
  const [mine, setMine] = useState<Mine | null>(null);
  const [activeId, setActiveId] = useState<string | null>(() => { try { return localStorage.getItem(ACTIVE_ORG_KEY); } catch { return null; } });
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<Array<{ id: string; name: string }> | null>(null);
  const [error, setError] = useState('');
  const searchReady = query.trim().length >= MIN_ORG_QUERY;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function load(select?: string) {
    const m = await request<Mine>('GET', '/me/organization');
    setMine(m);
    const all = m.organizations ?? (m.organization ? [m.organization] : []);
    const pick = all.find((o) => o.id === (select ?? activeId)) ?? all[0] ?? null;
    if (pick) { setActiveId(pick.id); try { localStorage.setItem(ACTIVE_ORG_KEY, pick.id); } catch { /* private mode */ } }
    setMembers(pick?.role === 'admin' ? await request<OrganizationMember[]>('GET', `/organizations/${pick.id}/members`) : []);
    void useAccount.getState().load();
  }
  useEffect(() => {
    const invitation=new URLSearchParams(window.location.search).get('join');
    if(invitation)void request<{id:string;name:string}>('GET',`/organizations/${encodeURIComponent(invitation)}/invitation`).then(setInvited).catch(e=>setError(errorMessage(e)));
    void load().catch((e) => setError(errorMessage(e)));
  }, []);
  async function act(fn: () => Promise<unknown>, message = '', select?: (result: unknown) => string | undefined) {
    setBusy(true);
    setError('');
    try {
      const result = await fn();
      await load(select?.(result));
      window.dispatchEvent(new Event('sightline:provider-settings'));
      setMessage(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const orgs = mine ? mine.organizations ?? (mine.organization ? [mine.organization] : []) : [];
  const org = orgs.find((o) => o.id === activeId) ?? orgs[0] ?? null;
  const showCreate = setupIntent !== 'join';
  const showFind = setupIntent !== 'create';

  const forms = mine ? (
            <>
              {invited && <div className="panel-stack"><h3>You’re invited to {invited.name}</h3><Button variant="primary" disabled={busy||mine.pending.some(p=>p.id===invited.id)} onClick={()=>void act(()=>request('POST',`/organizations/${invited.id}/join`),'Request sent. An admin will approve your membership.')}>Request to join {invited.name}</Button></div>}
              <p className="panel-lede">
                Create an organization to share an admin’s AeroAPI key, fleets and shoots, or find yours and request membership. An admin approves access; no invite code is required.
              </p>
              {mine.pending.length ? (
                <div className="org-pending">
                  {mine.pending.map((p) => (
                    <Notice key={p.id} tone="neutral" icon="clock" role="status">
                      <span>Awaiting approval · {p.name}</span>
                    </Notice>
                  ))}
                </div>
              ) : null}
              <div className={showCreate && showFind ? 'org-forms org-forms--two' : 'org-forms'}>
                {showFind ? (
                  <form
                    className="inline-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!searchReady) return;
                      void act(async () => setFound(await request('GET', '/organizations', undefined, { query: { q: query.trim() } })));
                    }}
                  >
                    <Field label="Find your organization" hint={`Type at least ${MIN_ORG_QUERY} characters of the name. An admin approves your request.`}>
                      {(id) => <TextInput id={id} icon="search" value={query} maxLength={80} onChange={(e) => { setQuery(e.target.value); if (e.target.value.trim().length < MIN_ORG_QUERY) setFound(null); }} placeholder="Organization name" />}
                    </Field>
                    <Button type="submit" variant="secondary" disabled={busy || !searchReady}>
                      Search organizations
                    </Button>
                  </form>
                ) : null}
                {showCreate ? (
                  <form
                    className="inline-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void act(() => request<Organization>('POST', '/organizations', { name }), 'Organization created. Add its aircraft data key under Data sources.', (r) => (r as Organization).id);
                    }}
                  >
                    <Field label="New organization name" hint="You become its admin and can approve members.">
                      {(id) => <TextInput id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Production company or team" />}
                    </Field>
                    <Button type="submit" variant="secondary" disabled={busy || !name.trim()}>
                      Create organization
                    </Button>
                  </form>
                ) : null}
              </div>
              {found ? (
                found.length ? (
                  <ul className="row-list" aria-label="Organizations found">
                    {found.map((o) => {
                      const requested = mine.pending.some((p) => p.id === o.id);
                      return (
                        <li className="org-member" key={o.id}>
                          <span className="org-member__avatar" aria-hidden>
                            {initials(o.name)}
                          </span>
                          <div className="org-member__who">
                            <strong>{o.name}</strong>
                          </div>
                          <div className="org-member__actions">
                            <Button size="sm" variant="primary" disabled={busy || requested} onClick={() => void act(() => request('POST', `/organizations/${o.id}/join`), 'Request sent to organization admins.')}>
                              Request to join
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="org-empty">
                    <Icon name="search" size={15} /> No organization matches “{query}”. Check the spelling, or ask your admin for the exact name.
                  </p>
                )
              ) : null}
            </>
  ) : null;

  return (
    <section id="organization" className="org-section">
      <Panel title="Organization" eyebrow="Team">
        <div className="panel-stack">
          {error ? (
            <Notice tone="error" role="alert">
              {error}
            </Notice>
          ) : null}
          {message ? (
            <Notice tone="success" role="status">
              {message}
            </Notice>
          ) : null}
          {!mine && !error ? <p className="muted">Loading your organization…</p> : null}
          {org ? (
            <>
              <div className="org-identity">
                <span className="org-identity__mark" aria-hidden>
                  {initials(org.name)}
                </span>
                <div className="org-identity__text">
                  <h2>{org.name}</h2>
                  <p className="muted">Fleet aircraft and new shoots are shared. You can open organization shoots without an invite code.</p>
                </div>
                <Chip size="sm" tone={org.role === 'admin' ? 'strong' : 'neutral'}>
                  {org.role === 'admin' ? 'Organization admin' : 'Organization member'}
                </Chip>
              </div>
              {orgs.length > 1 ? (
                <nav className="org-tabs" aria-label="Your organizations">
                  {orgs.map((o) => <Button key={o.id} size="sm" variant={o.id === org.id ? 'primary' : 'secondary'} aria-pressed={o.id === org.id} onClick={() => { setActiveId(o.id); try { localStorage.setItem(ACTIVE_ORG_KEY, o.id); } catch { /* private mode */ } void load(o.id); }}>{o.name}</Button>)}
                </nav>
              ) : null}
              <Notice tone="neutral" icon="check" title="Every feature included" role="status">
                Shoots and the shared fleet use this organization’s own aircraft data key (Data sources tab). Airview Studio has no paid plans.
              </Notice>
              <nav className="org-tabs" aria-label="Organization sections">
                {(['people','sources','fleet','rules'] as const).map(t=><Button key={t} size="sm" variant={tab===t?'primary':'secondary'} onClick={()=>setTab(t)} aria-pressed={tab===t}>{({people:'People',sources:'Data sources',fleet:'Fleet',rules:'Rules'})[t]}</Button>)}
              </nav>
              {tab==='people' && <div className="panel-stack">
                <h3>Invite people</h3>
                <p className="muted">Share this link with your crew. They sign in and request to join; an admin approves access below.</p>
                <Field label="Organization invitation link">{id=><TextInput id={id} readOnly value={publicUrl(`/organization?join=${org.id}`)} onFocus={e=>e.currentTarget.select()}/>}</Field>
                <Button variant="secondary" icon="copy" onClick={()=>void act(()=>navigator.clipboard.writeText(publicUrl(`/organization?join=${org.id}`)),'Invitation link copied. Share it with your crew.')}>Copy invite link</Button>
              </div>}
              {tab==='people' && members.length > 0 ? (
                <div className="org-members">
                  <div className="eyebrow">Members · {members.length}</div>
                  <ul className="row-list">
                    {members.map((m) => (
                      <li className="org-member" key={m.userId}>
                        <span className="org-member__avatar" aria-hidden>
                          {initials(m.displayName)}
                        </span>
                        <div className="org-member__who">
                          <strong>{m.displayName}</strong>
                          {m.status === 'pending' ? (
                            <Chip size="sm" tone="aging">
                              Awaiting approval
                            </Chip>
                          ) : (
                            <span className="muted small">{m.role === 'admin' ? 'Admin' : 'Member'}</span>
                          )}
                        </div>
                        <div className="org-member__actions">
                          {m.status === 'pending' ? (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={busy}
                              onClick={() => void act(() => request('PATCH', `/organizations/${org.id}/members/${m.userId}`, { status: 'active', role: 'member' }), 'Membership approved.')}
                            >
                              Approve
                            </Button>
                          ) : m.userId !== me?.id ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => void act(() => request('PATCH', `/organizations/${org.id}/members/${m.userId}`, { status: 'active', role: m.role === 'admin' ? 'member' : 'admin' }))}
                            >
                              {m.role === 'admin' ? 'Make member' : 'Make admin'}
                            </Button>
                          ) : (
                            <Chip size="sm" tone="accent">
                              You
                            </Chip>
                          )}
                          {m.userId !== me?.id ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm(`Remove ${m.displayName} from ${org.name}?`)) void act(() => request('DELETE', `/organizations/${org.id}/members/${m.userId}`));
                              }}
                            >
                              Remove
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {tab==='fleet' && <OrganizationFleet orgId={org.id} canManage={org.role==='admin'} />}
              {tab==='rules' && <OrganizationRules orgId={org.id} canManage={org.role==='admin'} onRename={load}/>}
              {tab==='people' && <div className="panel-actions panel-actions--start">
                <Button
                  variant="ghost"
                  icon="logout"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Leave ${org.name}? You will lose access to its fleet and shoots.`)) void act(() => request('DELETE', `/organizations/${org.id}/members/${me!.id}`));
                  }}
                >
                  Leave organization
                </Button>
              </div>}
              {tab==='sources' && <p className="muted">These sources serve the shared fleet and organization shoots. When no organization source is enabled, a shoot uses its owner’s configured account sources.</p>}
              <details className="org-more" open={!!invited}><summary>Create or join another organization</summary>{forms}</details>
            </>
          ) : mine ? (
            forms
          ) : null}
        </div>
      </Panel>
      {org && tab==='sources' ? <DataSourcesPanel key={org.id} organizationId={org.id} canManage={org.role === 'admin'} /> : null}
    </section>
  );
}
